import ListSubheader from "@mui/joy/ListSubheader";
import Option from "@mui/joy/Option";
import Select, { selectClasses } from "@mui/joy/Select";
import React, {
	useState,
	useEffect,
	useMemo,
} from "react";
import { useNavigate } from "react-router-dom";

import type {
	TDataset,
	TAgent,
	TAgentDatasetType,
	TAgentDatasetMetaType,
} from "../../types/agent";

import { ChevronDownIcon } from "../../assets/icons/chevronDownIcon";
import { ChevronRightIcon } from "../../assets/icons/chevronRightIcon";
import { DbIcon } from "../../assets/icons/dbIcon";
import { ErrorIcon } from "../../assets/icons/errorIcon";
import PaulLogo from "../../assets/svg/agent-paul.svg";
import { AgentStatusMessages } from "../../constants/agent";
import { READ_ONLY_ACTION_MESSAGE } from "../../constants/readOnly";
import {
	getConversations,
	getAgentMeta,
} from "../../redux/agent/agent.thunk";
import {
	useAppDispatch,
	useAppSelector,
} from "../../redux/store";
import { services } from "../../services";
import { LoaderWithText } from "../../shared/Loader";
import { toTenantPath } from "../../utils/tenantUtils";

const REGRESSION_AGENT_V3_ID = "regression_agent_v3";
const GENERAL_AGENT_ID = "general_agent";

const HIDDEN_AGENT_IDS = new Set([
	"regression_agent",
	"regression_agent_v2",
]);

const DATASET_TYPE_GROUPS: Array<{
	type: TAgentDatasetType;
	label: string;
	aliases: Array<TAgentDatasetMetaType>;
}> = [
	{
		type: "default_dataset",
		label: "Platform datasets",
		aliases: ["default_dataset", "assisted"],
	},
	{
		type: "global_dataset",
		label: "Global datasets",
		aliases: ["global_dataset"],
	},
	{
		type: "v2_approved_tenant_dataset",
		label: "Sanitized Ledger datasets",
		aliases: [
			"v2_approved_tenant_dataset",
			"sanitized_dataset",
		],
	},
	{
		type: "raw_uploaded_dataset",
		label: "Raw Ledger datasets",
		aliases: ["raw_uploaded_dataset", "raw_dataset"],
	},
	{
		type: "raw_uploaded_tape",
		label: "Caliper tapes",
		aliases: ["raw_uploaded_tape", "caliper"],
	},
];

const DATASET_TYPE_GROUP_BY_ALIAS: Partial<
	Record<TAgentDatasetMetaType, TAgentDatasetType>
> = DATASET_TYPE_GROUPS.reduce<
	Partial<Record<TAgentDatasetMetaType, TAgentDatasetType>>
>(
	(acc, group) => {
		group.aliases.forEach((alias) => {
			acc[alias] = group.type;
		});
		return acc;
	},
	{}
);

/**
 * Detect if a message is requesting lithe/model creation.
 * Returns true when the text looks like a model-building request so the UI
 * can auto-select regression_agent_v3.
 */
function isLitheCreationIntent(text: string): boolean {
	const lower = text.toLowerCase();
	const creationVerbs = [
		"create",
		"build",
		"train",
		"fit",
		"generate",
		"make",
	];
	const modelKeywords = [
		"model",
		"lithe",
		"transition",
		"probability",
		"regression",
		"early loss",
		"prepayment",
		"liquidation",
		"default model",
		"current to delinquent",
		"delinquent to current",
		"modification model",
		"c2d",
		"d2c",
		"risk factor",
	];
	const hasCreationVerb = creationVerbs.some((v) =>
		lower.includes(v)
	);
	const hasModelKeyword = modelKeywords.some((k) =>
		lower.includes(k)
	);
	return hasCreationVerb && hasModelKeyword;
}

type TNewChatProps = {
	readOnly?: boolean;
};

const NewChat: React.FC<TNewChatProps> = ({
	readOnly = false,
}) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const {
		defaultDatasetId,
		loadingAgents,
		agents,
		defaultAgentId,
	} = useAppSelector((state) => state.agent);
	const { activeTenant } = useAppSelector(
		(state) => state.tenant
	);

	const agentList = Object.values(agents).filter(
		(a) => !HIDDEN_AGENT_IDS.has(a.id)
	);
	const [selectedDataset, setSelectedDataset] =
		useState<TDataset | null>(null);
	const [selectedAgent, setSelectedAgent] =
		useState<TAgent | null>(null);
	const [userSelectedAgent, setUserSelectedAgent] =
		useState(false);
	const [inputValue, setInputValue] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Get datasets for the selected agent
	const getAgentDatasets = (
		agent: TAgent | null
	): Array<TDataset> => {
		if (!agent) return [];
		return agent.datasets;
	};

	const agentDatasets = getAgentDatasets(selectedAgent);
	const groupedAgentDatasets = useMemo(() => {
		const datasetsByType = new Map<
			TAgentDatasetType,
			Array<TDataset>
		>();
		const uncategorizedDatasets: Array<TDataset> = [];

		agentDatasets.forEach((dataset) => {
			const datasetType = dataset.type;
			const groupType: TAgentDatasetType | undefined = datasetType
				? DATASET_TYPE_GROUP_BY_ALIAS[datasetType]
				: undefined;

			if (!groupType) {
				uncategorizedDatasets.push(dataset);
				return;
			}

			const datasets = datasetsByType.get(groupType) ?? [];
			datasets.push(dataset);
			datasetsByType.set(groupType, datasets);
		});

		const groups = DATASET_TYPE_GROUPS.flatMap((group) => {
			const datasets = datasetsByType.get(group.type) ?? [];
			return datasets.length > 0
				? [{ label: group.label, datasets }]
				: [];
		});

		return uncategorizedDatasets.length > 0
			? [
					...groups,
					{
						label: "Other datasets",
						datasets: uncategorizedDatasets,
					},
				]
			: groups;
	}, [agentDatasets]);
	const selectedDatasetIsAvailable = Boolean(
		selectedDataset &&
		agentDatasets.some(
			(dataset) => dataset.id === selectedDataset.id
		)
	);
	const hasPrompt = inputValue.trim().length > 0;
	const newChatValidationMessage =
		hasPrompt && !selectedAgent
			? "Select an agent before starting a chat."
			: hasPrompt && !selectedDatasetIsAvailable
				? agentDatasets.length === 0
					? "No datasets are available for the selected agent."
					: "Select a dataset before starting a chat."
				: null;
	const isSendDisabled =
		readOnly ||
		isLoading ||
		!hasPrompt ||
		!selectedAgent ||
		!selectedDatasetIsAvailable;
	const sendButtonTitle =
		readOnly
			? READ_ONLY_ACTION_MESSAGE
			: (newChatValidationMessage ??
				(!hasPrompt
					? "Enter a prompt to start a chat."
					: "Start chat"));

	useEffect(() => {
		void dispatch(getAgentMeta());
	}, [dispatch]);

	useEffect(() => {
		if (!selectedAgent) {
			if (selectedDataset) {
				setSelectedDataset(null);
			}
			return;
		}

		if (agentDatasets.length === 0) {
			if (selectedDataset) {
				setSelectedDataset(null);
			}
			return;
		}

		const matchingSelectedDataset = selectedDataset
			? agentDatasets.find(
					(dataset) => dataset.id === selectedDataset.id
				)
			: null;

		if (matchingSelectedDataset) {
			if (matchingSelectedDataset !== selectedDataset) {
				setSelectedDataset(matchingSelectedDataset);
			}
			return;
		}

		const defaultDataset =
			agentDatasets.find(
				(d: TDataset) => d.id === defaultDatasetId
			) ?? agentDatasets[0];
		setSelectedDataset(defaultDataset);
	}, [
		agentDatasets,
		defaultDatasetId,
		selectedAgent,
		selectedDataset,
	]);

	useEffect(() => {
		if (agentList.length > 0 && !selectedAgent) {
			const defaultAgent =
				agentList.find((a) => a.id === defaultAgentId) ??
				agentList[0];
			setSelectedAgent(defaultAgent);
		}
	}, [agentList, defaultAgentId, selectedAgent]);

	// Auto-route to regression_agent_v3 when user types a lithe-creation request,
	// unless the user has manually chosen a different agent.
	useEffect(() => {
		if (userSelectedAgent || agentList.length === 0) return;
		const targetId = isLitheCreationIntent(inputValue)
			? REGRESSION_AGENT_V3_ID
			: (defaultAgentId ?? GENERAL_AGENT_ID);
		const target = agentList.find((a) => a.id === targetId);
		if (target && target.id !== selectedAgent?.id) {
			setSelectedAgent(target);
		}
	}, [
		inputValue,
		userSelectedAgent,
		agentList,
		defaultAgentId,
		selectedAgent,
	]);

	const handleSendMessage = async () => {
		const trimmedInput = inputValue.trim();
		if (readOnly) {
			return;
		}
		if (!trimmedInput) {
			return;
		}

		if (!selectedAgent) {
			setError("Select an agent before starting a chat.");
			return;
		}

		if (!selectedDatasetIsAvailable || !selectedDataset) {
			setError(
				agentDatasets.length === 0
					? "No datasets are available for the selected agent."
					: "Select a dataset before starting a chat."
			);
			return;
		}

		setIsLoading(true);
		setError(null);
		try {
			const data =
				await services.agentServiceInstance.sendMessageToNewConversation(
					trimmedInput,
					selectedDataset.id,
					selectedAgent.id,
					{ module: "native_paul" }
				);
			void dispatch(getConversations());
			navigate(
				toTenantPath(
					`/agent/chat/${data.conv_id}`,
					activeTenant
				)
			);
		} catch (error: unknown) {
			console.error("Error sending message:", error);
			const responseError = error as {
				data?: { msg?: unknown };
			};
			const errorMessage =
				typeof responseError.data?.msg === "string"
					? responseError.data.msg
					: "An unexpected error occurred. Please try again.";
			setError(errorMessage);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="agent-welcome-container">
			<img
				src={PaulLogo}
				alt="Paul Logo"
				style={{
					width: "60px",
					height: "60px",
				}}
			/>
			<h1 style={{ marginBottom: "20px" }}>
				I'm Paul, how can I help today?
			</h1>
			{error && (
				<div className="agent-error-message">
					<ErrorIcon />
					<span>{error}</span>
				</div>
			)}
			{loadingAgents ? (
				<div className="agent-thinking-container">
					<LoaderWithText
						withText={true}
						text="Loading..."
						loaderWidth="30"
					/>
				</div>
			) : isLoading ? (
				<div className="agent-thinking-container">
					<div className="agent-thinking-spinner"></div>
					<p>{AgentStatusMessages.PENDING}</p>
				</div>
			) : (
				<div className="agent-new-chat-input-container">
					<div className="agent-input-container">
						<textarea
							className="agent-input-new-chat"
							placeholder={
								readOnly ? "Read-only view" : "Ask anything"
							}
							value={inputValue}
							onChange={(e) => {
								if (readOnly) return;
								setInputValue(e.target.value);
								setError(null);
								if (!e.target.value.trim()) {
									setUserSelectedAgent(false);
								}
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter" && !e.shiftKey) {
									e.preventDefault();
									void handleSendMessage();
								}
							}}
							rows={1}
							disabled={readOnly}
							style={{ resize: "none" }}
						/>
					</div>
					<div className="agent-input-actions-container">
						<div className="agent-input-actions-left">
							<Select
								placeholder="Select an agent"
								indicator={<ChevronDownIcon />}
								value={selectedAgent?.id ?? ""}
								disabled={readOnly}
								title={
									readOnly
										? READ_ONLY_ACTION_MESSAGE
										: undefined
								}
								onChange={(_, newValue) => {
									if (readOnly) return;
									const selected = agentList.find(
										(a) => a.id === newValue
									);
									setSelectedAgent(selected ?? null);
									setUserSelectedAgent(true);
									setError(null);
								}}
								sx={{
									minWidth: 150,
									flexShrink: 0,
									[`& .${selectClasses.indicator}`]: {
										transition: "0.2s",
										[`&.${selectClasses.expanded}`]: {
											transform: "rotate(-180deg)",
										},
									},
								}}
							>
								{agentList.map((agent) => (
									<Option
										key={agent.id}
										value={agent.id}
									>
										{agent.name}
									</Option>
								))}
							</Select>
							<Select
								placeholder="Select a dataset"
								startDecorator={<DbIcon />}
								indicator={<ChevronDownIcon />}
								value={selectedDataset?.id ?? ""}
								disabled={readOnly}
								title={
									readOnly
										? READ_ONLY_ACTION_MESSAGE
										: undefined
								}
								onChange={(_, newValue) => {
									if (readOnly) return;
									const selected = agentDatasets.find(
										(d: TDataset) => d.id === newValue
									);
									setSelectedDataset(selected ?? null);
									setError(null);
								}}
								sx={{
									minWidth: 0,
									width: 300,
									maxWidth: "50%",
									flex: "0 1 300px",
									[`& .${selectClasses.button}`]: {
										minWidth: 0,
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									},
									[`& .${selectClasses.indicator}`]: {
										flexShrink: 0,
										transition: "0.2s",
										[`&.${selectClasses.expanded}`]: {
											transform: "rotate(-180deg)",
										},
									},
								}}
								slotProps={{
									listbox: {
										className: "agent-dataset-select-listbox",
									},
								}}
							>
								{groupedAgentDatasets.map((group) => (
									<React.Fragment key={group.label}>
										<ListSubheader className="agent-dataset-select-heading">
											{group.label}
										</ListSubheader>
										{group.datasets.map(
											(dataset: TDataset) => (
												<Option
													key={dataset.id}
													value={dataset.id}
													className="agent-dataset-select-option"
													title={dataset.name}
												>
													{dataset.name}
												</Option>
											)
										)}
									</React.Fragment>
								))}
							</Select>
						</div>
						<button
							className="icon-button send-button"
							onClick={handleSendMessage}
							disabled={isSendDisabled}
							title={sendButtonTitle}
							aria-label={sendButtonTitle}
						>
							<ChevronRightIcon />
						</button>
					</div>
					{newChatValidationMessage && (
						<p className="agent-new-chat-validation">
							{newChatValidationMessage}
						</p>
					)}
				</div>
			)}
			<p className="disclaimer new-chat-disclaimer">
				Paul can make mistakes. Check important info.
			</p>
		</div>
	);
};

export default NewChat;
